import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import io from 'socket.io-client';
import Theme from '../../../styles/Theme/Theme';
import { websocketPort } from '../../../server/websocketPort';

const ControlPanelWrapper = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: #1a1a2e;
  padding: 30px;
  overflow-y: auto;
`;

const Header = styled.div`
  color: white;
  margin-bottom: 40px;
  
  h1 {
    font-size: 32px;
    margin: 0 0 10px 0;
    color: #00d4ff;
  }
  
  p {
    margin: 0;
    color: #888;
    font-size: 16px;
  }
`;

const VideowallPreview = styled.div`
  background: #2a2a3e;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
  border: 2px solid #3a3a5e;
`;

const PreviewTitle = styled.h2`
  color: white;
  font-size: 20px;
  margin: 0 0 20px 0;
`;

const GridPreview = styled.div`
  display: grid;
  grid-template-columns: ${props => props.zonaD ? '1.3fr 4fr 0.8fr' : '1.3fr 4fr'};
  grid-template-rows: 3fr 1.2fr;
  gap: 8px;
  height: 300px;
  background: #16213e;
  border-radius: 8px;
  padding: 10px;
`;

const ZonePreviewBox = styled.div`
  background: ${props => props.visible ? '#0f3460' : '#1a1a1a'};
  border: 2px solid ${props => props.visible ? '#00d4ff' : '#444'};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.visible ? '#00d4ff' : '#666'};
  font-weight: bold;
  font-size: 18px;
  grid-column: ${props => props.gridColumn};
  grid-row: ${props => props.gridRow};
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #00ff9d;
    transform: scale(1.02);
  }
`;

const ControlsSection = styled.div`
  background: #2a2a3e;
  border-radius: 12px;
  padding: 20px;
  border: 2px solid #3a3a5e;
`;

const ControlGroup = styled.div`
  margin-bottom: 25px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ControlLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
  font-size: 18px;
  padding: 15px 20px;
  background: #16213e;
  border-radius: 8px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #1f2b4a;
  }
`;

const ToggleSwitch = styled.div`
  position: relative;
  width: 60px;
  height: 30px;
  background: ${props => props.checked ? '#00d4ff' : '#555'};
  border-radius: 15px;
  transition: background 0.3s ease;
  cursor: pointer;
  
  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${props => props.checked ? '33px' : '3px'};
    width: 24px;
    height: 24px;
    background: white;
    border-radius: 50%;
    transition: left 0.3s ease;
  }
`;

const StatusIndicator = styled.div`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.connected ? '#00ff9d' : '#ff4757'};
  margin-right: 10px;
  animation: ${props => props.connected ? 'pulse 2s infinite' : 'none'};
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const ConnectionStatus = styled.div`
  display: flex;
  align-items: center;
  color: ${props => props.connected ? '#00ff9d' : '#ff4757'};
  font-size: 14px;
  margin-top: 20px;
`;

const TestButton = styled.button`
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 20px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ControlPanel = () => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [zoneConfig, setZoneConfig] = useState({
    zoneA: { visible: true, name: 'Zone A' },
    zoneB: { visible: true, name: 'Zone B' },
    zoneC: { visible: true, name: 'Zone C' },
    zoneX: { visible: true, name: 'Zone X' },
    zoneZ: { visible: true, name: 'Zone Z' },
    zoneD: { visible: false, name: 'Zone D' }
  });

  useEffect(() => {
    // Connect to WebSocket server
    const socketInstance = io(websocketPort, {
      transports: ['websocket']
    });

    socketInstance.on('connect', () => {
      console.log('Connected to WebSocket server:', websocketPort);
      setConnected(true);
    });

    socketInstance.on('disconnect', () => {
      console.log('Disconnected from WebSocket server');
      setConnected(false);
    });

    socketInstance.on('config_updated', (config) => {
      console.log('Config updated from server:', config);
      setZoneConfig(config);
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
    };
  }, []);

  const toggleZone = (zoneName) => {
    const newConfig = {
      ...zoneConfig,
      [zoneName]: {
        ...zoneConfig[zoneName],
        visible: !zoneConfig[zoneName].visible
      }
    };
    
    setZoneConfig(newConfig);
    
    if (socket && connected) {
      socket.emit('update_zone_config', newConfig);
      console.log('Sent config update:', newConfig);
    }
  };

  const sendTestSignal = () => {
    if (socket && connected) {
      socket.emit('test_signal', { timestamp: Date.now() });
      console.log('Test signal sent');
    }
  };

  return (
    <ControlPanelWrapper>
      <Header>
        <h1>Videowall Control Panel</h1>
      </Header>

      <VideowallPreview>
        <PreviewTitle>Videowall Layout Preview</PreviewTitle>
        <GridPreview zonaD={zoneConfig.zoneD.visible}>
          <ZonePreviewBox 
            visible={zoneConfig.zoneA.visible}
            gridColumn="1"
            gridRow="1 / -1"
            onClick={() => toggleZone('zoneA')}
          >
            Zone A
          </ZonePreviewBox>
          
          <ZonePreviewBox 
            visible={zoneConfig.zoneB.visible}
            gridColumn="2"
            gridRow="1"
            onClick={() => toggleZone('zoneB')}
          >
            Zone B
          </ZonePreviewBox>
          
          <ZonePreviewBox 
            visible={zoneConfig.zoneC.visible}
            gridColumn="2"
            gridRow="2"
            onClick={() => toggleZone('zoneC')}
          >
            Zone C
          </ZonePreviewBox>
          
          {zoneConfig.zoneD.visible && (
            <ZonePreviewBox 
              visible={zoneConfig.zoneD.visible}
              gridColumn="3"
              gridRow="1 / -1"
              onClick={() => toggleZone('zoneD')}
            >
              Zone D
            </ZonePreviewBox>
          )}
        </GridPreview>
      </VideowallPreview>

      <ControlsSection>
        <PreviewTitle>Zone Controls</PreviewTitle>
        
        {Object.entries(zoneConfig).map(([key, zone]) => (
          <ControlGroup key={key}>
            <ControlLabel onClick={() => toggleZone(key)}>
              <span>{zone.name}</span>
              <ToggleSwitch checked={zone.visible} />
            </ControlLabel>
          </ControlGroup>
        ))}

        <TestButton onClick={sendTestSignal}>
          Send Test Signal
        </TestButton>

        <ConnectionStatus connected={connected}>
          <StatusIndicator connected={connected} />
          {connected ? 'Connected to Videowall' : 'Disconnected'}
        </ConnectionStatus>
      </ControlsSection>
    </ControlPanelWrapper>
  );
};

export default ControlPanel;
